import { useState, useEffect, useRef } from 'react';
import { Button, Form, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMemorial } from '../../../context/memorial.provider';
import useApi from '../../../utils/api';
import { useParams, useSearchParams } from 'react-router-dom';
import SelectGif from './steps/SelectGif';
import TermsOfUse from './steps/TermsOfUse';
import SuccessMessage from '../../SuccessMessage';
import Loader from '../../../DLS/Loader';
import { useIsMobile } from '../../../utils/hooks';

export const LeaveDetailForm = ({
  deceasedName,
  resetValues,
  onClose,
}: {
  deceasedName: string;
  resetValues: boolean;
  onClose: () => void;
}) => {
  const { id: userId } = useMemorial();
  const isLoggedUser = !!userId;
  const [selectedGif, setSelectedGif] = useState<number>(0);
  const [isLoadingGifs, setIsLoadingGifs] = useState<boolean>(true);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [showRequiredErrorInCaptcha, setShowRequiredErrorInCaptcha] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);

  const [formTermsOfUse] = Form.useForm();
  const { api } = useApi();
  const { t } = useTranslation();
  let [searchParams] = useSearchParams();
  const URLParams = useParams();
  const check1 = Form.useWatch('check1', formTermsOfUse);
  const endDivRef = useRef<HTMLDivElement>(null);
  const endDivMobileRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const isDisabledMainButton = !selectedGif || !check1 || (isLoggedUser ? false : !captchaToken);

  useEffect(() => {
    if (isMobile) {
      endDivMobileRef?.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      endDivRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isMobile, selectedGif]);

  useEffect(() => {
    if (resetValues) {
      formTermsOfUse.resetFields();
      setSelectedGif(0);
      setIsLoadingGifs(false);
      setCaptchaToken('');
      setShowRequiredErrorInCaptcha(false);
      setIsSaving(false);
    }
  }, [formTermsOfUse, resetValues]);

  const onSaveGif = () => {
    formTermsOfUse.submit();
    !captchaToken && setShowRequiredErrorInCaptcha(true);
    if (!isDisabledMainButton) {
      setIsSaving(true);
      const payload = {
        gifs_id: selectedGif,
        personal_information_id: String(URLParams.id),
      };
      setIsSaving(true);
      api()
        .post('free/gifs-personal-info', payload)
        .then((response) => {
          if (response.status === 200) {
            setShowSuccessMessage(true);
          }
        })
        .finally(() => setIsSaving(false));
    }
  };

  return (
    <>
      <div
        className={`${
          isLoadingGifs || showSuccessMessage ? 'hidden' : ''
        } flex h-[100%] w-[100%] flex-1 flex-col md:h-auto`}
      >
        <div className="flex-1 px-[16px] md:overflow-y-auto md:px-[0px]">
          <h1 className="mb-[56px] mt-[72px] hidden px-[112px] text-center md:block">
            {t('leaveDetail.title')}
          </h1>
          <div className="mb-[140px] md:mb-[16px] md:px-[112px]">
            <div className="mt-[24px">
              <SelectGif
                selectedGif={selectedGif}
                setIsLoadingGifs={setIsLoadingGifs}
                setSelectedGif={setSelectedGif}
              />
            </div>
            <TermsOfUse
              captchaToken={captchaToken}
              form={formTermsOfUse}
              setCaptchaToken={setCaptchaToken}
              setShowRequiredErrorInCaptcha={setShowRequiredErrorInCaptcha}
              showCaptcha={!isLoggedUser}
              showRequiredErrorInCaptcha={showRequiredErrorInCaptcha}
            />
            <div ref={endDivRef}></div>
          </div>
        </div>
        <Row className="footerButtons md:box-border md:flex md:w-[100%] md:justify-end md:p-[24px] md:shadow-up">
          <Button className="md:mr-[8px]" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            onClick={onSaveGif}
            className={`mb-[8px] md:mb-0 ${isDisabledMainButton ? 'primaryDisabled' : ''}`}
            loading={isSaving}
          >
            {t('common.save')}
          </Button>
        </Row>
        <div ref={endDivMobileRef}></div>
      </div>
      {isLoadingGifs && (
        <div className="flex h-full flex-1 items-center justify-center md:h-auto">
          <Loader />
        </div>
      )}
      {showSuccessMessage && (
        <SuccessMessage
          bodyText1={t('leaveDetail.success.bodyText1', {
            value: deceasedName || searchParams.get('name'),
          })}
          buttonText={t('leaveDetail.success.buttonText')}
          onClose={onClose}
          title={t('leaveDetail.success.title')}
        />
      )}
    </>
  );
};

export default LeaveDetailForm;
